import { useState, useEffect, useCallback } from 'react';
import { Cpu, RefreshCw, ExternalLink, Play, Search, CheckCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.21 (Module 7): Tech Scout self-audit engine. Primary: GET /api/scout/findings
// (Claude audits KORINTH's own source per area for working-as-designed / efficient / improvable,
// grounded in the operator's Windows stack + decisions). POST /api/scout/audit runs a full sweep
// or a single-area deep-dive. Secondary: GET /api/scout trending HN/GitHub feed.

const PRIORITY_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280', none: '#10b981' };
const ACCENT = '#22d3ee';
const MUTED = '#6b7280';

function scoreColor(s) { return s >= 60 ? '#ef4444' : s >= 35 ? '#f59e0b' : '#10b981'; }
function pColor(p) { return PRIORITY_COLOR[p] || MUTED; }

function Bool({ label, val }) {
  const ok = val === true;
  const unknown = val == null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono" style={{ color: unknown ? MUTED : ok ? '#10b981' : '#f59e0b' }}>
      {unknown ? null : ok ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}: {unknown ? '?' : ok ? 'yes' : 'no'}
    </span>
  );
}

function FindingCard({ f, onReaudit, busy }) {
  if (f.pending) {
    return (
      <div className="rounded-lg border border-korinth-border/30 bg-korinth-bg/30 p-3 flex items-center gap-3">
        <span className="text-sm text-korinth-muted">{f.area_label}</span>
        <span className="text-[10px] text-korinth-muted/60">not audited yet</span>
        <button onClick={() => onReaudit(f.area_key)} disabled={busy} className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded border border-korinth-border/40 text-korinth-accent hover:bg-korinth-accent/10 disabled:opacity-40">Audit</button>
      </div>
    );
  }
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: `${pColor(f.top_priority)}44`, backgroundColor: `${pColor(f.top_priority)}0a` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-white">{f.area_label}</span>
        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ color: pColor(f.top_priority), backgroundColor: `${pColor(f.top_priority)}1f` }}>{f.top_priority}</span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: scoreColor(f.score), backgroundColor: `${scoreColor(f.score)}1f` }}>opportunity {f.score}</span>
        <Bool label="as-designed" val={f.working_as_designed} />
        <Bool label="efficient" val={f.efficient} />
        <button onClick={() => onReaudit(f.area_key)} disabled={busy} className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded border border-korinth-border/40 text-korinth-accent hover:bg-korinth-accent/10 disabled:opacity-40">Re-audit</button>
      </div>
      {f.assessment && <div className="text-[11px] text-korinth-text mt-1.5">{f.assessment}</div>}
      {f.improvements?.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {f.improvements.map((imp, i) => (
            <div key={i} className="text-[11px] pl-2 border-l-2" style={{ borderColor: pColor(imp.priority) }}>
              <span className="font-mono uppercase text-[9px] px-1 py-0.5 rounded mr-1" style={{ color: ACCENT, backgroundColor: `${ACCENT}1f` }}>{imp.type}</span>
              <span className="text-white font-semibold">{imp.what}</span>
              {imp.os_ok === false && <span className="text-[9px] text-amber-400 ml-1">(Windows alt)</span>}
              <span className="text-[9px] font-mono ml-1" style={{ color: pColor(imp.priority) }}>{imp.priority}</span>
              {imp.why && <div className="text-korinth-muted mt-0.5">{imp.why}</div>}
            </div>
          ))}
        </div>
      )}
      {f.files_audited?.length > 0 && <div className="text-[9px] text-korinth-muted/60 font-mono mt-2">read: {f.files_audited.join(', ')}</div>}
    </div>
  );
}

export default function TechScout() {
  const { setPage } = usePage();
  useEffect(() => { setPage('scout'); }, []);

  const [data, setData] = useState(null);
  const [trending, setTrending] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // 22.7.92: kept separate from `msg` so the pause reads as a state, not as one
  // more line of progress chatter in the same muted grey.
  const [paused, setPaused] = useState(null);
  const [showTrending, setShowTrending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/scout/findings');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  async function audit(areas) {
    // 22.7.92 (codex R4-F3): clear the paused notice with the other per-request
    // resets, so a stale amber banner cannot sit above a new in-flight or
    // failed run and describe the wrong request.
    setBusy(true); setErr(null); setPaused(null);
    setMsg(areas ? `Auditing ${areas[0]} in the background...` : 'Running full audit in the background (reads the codebase, ~1-2 min)...');
    try {
      // Fire-and-forget: the sweep runs server-side; we poll findings as they land.
      const r = await fetch('/api/scout/audit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(areas ? { areas } : {}) });
      const d = await r.json();
      if (!r.ok) { setMsg(`Audit: ${d.error || `HTTP ${r.status}`}`); setBusy(false); return; }
      // Sprint 22.7.92: a paused sweep never starts, so falling through to the
      // poll loop below would find auditing:false on the first tick and report
      // "Audit done: 0/N areas" - telling the operator a sweep completed that
      // was never run. Branch out before polling.
      if (d.skipped && d.reason === 'llm_paused') {
        setPaused('Paused - LLM lanes are switched off (KORINTH_PAUSE_LLM). The audit was not started; findings below are from the last completed sweep.');
        setMsg(null); setBusy(false); return;
      }
      if (d.started === false && d.running) setMsg('An audit is already running - watching it...');
      for (let i = 0; i < 40; i++) {
        await new Promise(res => setTimeout(res, 5000));
        const fr = await fetch('/api/scout/findings');
        if (!fr.ok) continue;
        const fd = await fr.json();
        setData(fd);
        const done = fd.findings?.length || 0;
        const mode = fd.web_search_active === false ? ' - knowledge-only (enable web search in the Claude Console for live research)' : ' - web-search-backed';
        if (!fd.auditing) {
          setMsg(areas ? `Re-audit of ${areas[0]} done${mode}.` : `Audit done: ${done}/${fd.areas_total} areas${mode}.`);
          break;
        }
        setMsg(areas ? `Re-auditing ${areas[0]} (single area)...` : `Auditing... ${done}/${fd.areas_total} areas done`);
      }
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  async function loadTrending() {
    try { const r = await fetch('/api/scout'); if (r.ok) setTrending(await r.json()); } catch { /* ignore */ }
  }

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showTrending && !trending) loadTrending(); }, [showTrending, trending]);

  const findings = data?.findings || [];
  const pending = data?.pending || [];
  const all = [...findings, ...pending];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Cpu className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Tech &amp; AI Scout</h1>
        <span className="text-korinth-muted text-sm">Audits KORINTH's own processes for tools, connectors, and approaches that improve them</span>
        <div className="ml-auto flex items-center gap-2">
          {data && (
            <span className="text-[10px] font-mono px-2 py-1 rounded" style={{ color: data.web_search_active ? '#10b981' : '#f59e0b', backgroundColor: data.web_search_active ? '#10b98119' : '#f59e0b19' }}>
              {data.web_search_active ? 'web-search on' : 'knowledge-only'}
            </span>
          )}
          <button onClick={() => audit(null)} disabled={busy} className="flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-accent/40 text-korinth-accent hover:bg-korinth-accent/10 transition-colors disabled:opacity-50">
            <Search className={`w-3.5 h-3.5 ${busy ? 'animate-pulse' : ''}`} />
            {busy ? 'Auditing...' : 'Run full audit'}
          </button>
          <button onClick={load} className="flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-border/40 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}
      {paused && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-mono text-amber-400">
          {paused}
        </div>
      )}
      {msg && <div className="text-[11px] text-korinth-muted font-mono">{msg}</div>}
      {data && (
        <div className="text-[10px] text-korinth-muted font-mono">
          {findings.length}/{data.areas_total} areas audited{data.last_run ? ` - last run ${new Date(data.last_run).toLocaleString()}` : ' - none yet, click "Run full audit"'} - weekly auto-sweep Sunday 14:00 Dubai
        </div>
      )}

      {/* Audit findings (primary) */}
      <ModuleContainer title="System audit" subtitle="Each KORINTH process, judged on the real source: working as designed? efficient? what would improve it? Biggest opportunity first.">
        {all.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No areas. Click "Run full audit".'}</div>
        ) : (
          <div className="space-y-2.5">
            {all.map((f) => <FindingCard key={f.area_key} f={f} onReaudit={(k) => audit([k])} busy={busy} />)}
          </div>
        )}
      </ModuleContainer>

      {/* Trending discovery (secondary) */}
      <ModuleContainer title={null}>
        <button onClick={() => setShowTrending(s => !s)} className="flex items-center gap-2 text-sm text-korinth-text hover:text-korinth-accent">
          {showTrending ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          What's new (HackerNews + GitHub trending)
          <span className="text-[10px] text-korinth-muted">secondary</span>
        </button>
        {showTrending && (
          <div className="mt-3 space-y-2">
            {!trending ? (
              <div className="text-[11px] text-korinth-muted">Loading...</div>
            ) : (trending.korinth?.length || 0) + (trending.everyday?.length || 0) === 0 ? (
              <div className="text-[11px] text-korinth-muted">No trending items yet (the weekly sweep populates this).</div>
            ) : (
              [...(trending.korinth || []), ...(trending.everyday || [])].slice(0, 20).map((it) => (
                <div key={it.id} className="flex items-start gap-2 text-[11px]">
                  <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-korinth-border/40 text-korinth-muted shrink-0">{it.source}</span>
                  <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-korinth-text hover:text-korinth-accent flex items-center gap-1 min-w-0">
                    <span className="truncate">{it.title}</span><ExternalLink className="w-3 h-3 shrink-0 text-korinth-muted" />
                  </a>
                  <span className="ml-auto text-[9px] font-mono text-korinth-muted shrink-0">{it.audience}</span>
                </div>
              ))
            )}
          </div>
        )}
      </ModuleContainer>
    </div>
  );
}
